import { Injectable, inject } from '@angular/core';
import { Observable, catchError, of } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { PagedResult } from '../../core/models/api.models';
import {
  CreateManualTimeEntryRequest,
  StartTimerRequest,
  TimeEntryDto,
  TimeEntryListParams,
  TimesheetDto
} from './time-tracking.models';

@Injectable({ providedIn: 'root' })
export class TimeTrackingService {
  private readonly api = inject(ApiService);
  private readonly base = 'v1/time-entries';

  list(params: TimeEntryListParams = {}): Observable<PagedResult<TimeEntryDto>> {
    return this.api.getPaged<TimeEntryDto>(this.base, params as never);
  }

  createManual(body: CreateManualTimeEntryRequest): Observable<TimeEntryDto> {
    return this.api.post<TimeEntryDto>(this.base, body);
  }

  startTimer(body: StartTimerRequest): Observable<TimeEntryDto> {
    return this.api.post<TimeEntryDto>(`${this.base}/timer/start`, body);
  }

  pauseTimer(entryId: string): Observable<TimeEntryDto> {
    return this.api.post<TimeEntryDto>(`${this.base}/timer/${entryId}/pause`, {});
  }

  resumeTimer(entryId: string): Observable<TimeEntryDto> {
    return this.api.post<TimeEntryDto>(`${this.base}/timer/${entryId}/resume`, {});
  }

  stopTimer(entryId: string): Observable<TimeEntryDto> {
    return this.api.post<TimeEntryDto>(`${this.base}/timer/${entryId}/stop`, {});
  }

  updateIdle(entryId: string, body: { idleSeconds?: number; isIdle?: boolean }): Observable<TimeEntryDto> {
    return this.api.post<TimeEntryDto>(`${this.base}/timer/${entryId}/idle`, body);
  }

  recordActivity(entryId: string): Observable<TimeEntryDto> {
    return this.api.post<TimeEntryDto>(`${this.base}/timer/${entryId}/activity`, {});
  }

  submit(entryId: string): Observable<TimeEntryDto> {
    return this.api.post<TimeEntryDto>(`${this.base}/${entryId}/submit`, {});
  }

  approve(entryId: string): Observable<TimeEntryDto> {
    return this.api.post<TimeEntryDto>(`${this.base}/${entryId}/approve`, {});
  }

  reject(entryId: string, reason?: string): Observable<TimeEntryDto> {
    return this.api.post<TimeEntryDto>(`${this.base}/${entryId}/reject`, { reason });
  }

  getTimesheet(params?: { from?: string; to?: string; employeeId?: string }): Observable<TimesheetDto> {
    return this.api.get<TimesheetDto>(`${this.base}/timesheet`, params as never).pipe(
      catchError(() => of({
        employeeId: '',
        from: '',
        to: '',
        fromDate: '',
        toDate: '',
        totalHours: 0,
        billableHours: 0,
        nonBillableHours: 0,
        days: []
      }))
    );
  }

  /** Sum logged hours for an issue (falls back to client-side list when total endpoint is unavailable). */
  totalHoursForIssue(issueId: string): Observable<number> {
    return new Observable((subscriber) => {
      this.list({ issueId, page: 1, pageSize: 500 }).subscribe({
        next: (page) => {
          const total = (page.items ?? []).reduce((sum, e) => sum + (e.isRunning ? 0 : Number(e.hours ?? 0)), 0);
          subscriber.next(total);
          subscriber.complete();
        },
        error: () => {
          subscriber.next(0);
          subscriber.complete();
        }
      });
    });
  }
}
